import 'twin.macro';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@teetsh/app/src/components/Buttons/Button';
import _isEmpty from 'lodash-es/isEmpty';
import { AIWrapper } from '@teetsh/app/src/components/AI/AIWrapper';
import { useGenerateRemark } from '../useGenerateRemark';
import { ReworkButton } from '../ReworkButton';
import MagicWandIcon from '@teetsh/app/src/components/Icons/icons/MagicWand';
import tw from 'twin.macro';

interface Props {
  value: string;
  visible?: boolean;
  onChange?: (v: string) => void;
  livretEleveId: string;
  children: React.ReactNode;
  matiereId?: string;
  small?: boolean;
  onLoadingStateChange?: (isLoading: boolean) => void;
  onCancel?: () => void;
}

export function GlobalRemarkAIWrapper(props: Props) {
  const {
    children,
    onChange,
    value,
    matiereId,
    onLoadingStateChange,
    onCancel,
  } = props;
  const { t } = useTranslation(['common', 'carnet']);

  const baseAiHook = useGenerateRemark({
    livretEleveId: props.livretEleveId,
    matiereId,
    onComplete: () => {},
  });

  // Create enhanced hook with custom stop behavior
  const aiHook = {
    ...baseAiHook,
    stop: () => {
      baseAiHook.stop();
      onCancel?.();
    },
  };

  // Notify parent of loading state changes
  useEffect(() => {
    onLoadingStateChange?.(aiHook.isLoading);
  }, [aiHook.isLoading, onLoadingStateChange]);

  const handleGenerate = () => {
    aiHook.startGeneration();
  };

  // Create ReworkButton as additional action
  const additionalActions = aiHook.data ? (
    <ReworkButton
      isFinish={!aiHook.isLoading}
      text={aiHook.data}
      livretEleveId={props.livretEleveId}
      startStream={() => aiHook.startGeneration()}
      reset={aiHook.reset}
    />
  ) : undefined;

  return (
    <AIWrapper
      value={value}
      onChange={onChange}
      aiHook={aiHook}
      additionalActions={additionalActions}
      showTypingEffect={true}
    >
      <>
        {children}
        {props.visible && (
          <AIWrapper.Actions tw="absolute right-2 bottom-6 text-right">
            <Button
              tw="bg-white"
              onClick={handleGenerate}
              small
              css={[props.small && tw`text-xs py-0.5`]}
              loading={aiHook.isLoading}
              icon={MagicWandIcon as any}
              className="ai-generation-button"
              outlined
            >
              {_isEmpty(value)
                ? t('common:buttons.suggestionIA')
                : t('common:buttons.reworkWithAI')}
            </Button>
          </AIWrapper.Actions>
        )}
      </>
    </AIWrapper>
  );
}
